/* eslint-disable no-console */
import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { NotAuthorized, RemoteData } from 'rf-comp';
import {
  get, isAuthorized, useInterval
} from 'rf-lib';
import {
  Button,
  Page,
  TextLarge,
} from 'rf-ui';

import { useLocation, useParams } from 'react-router-dom';
import GameRenderer from 'rf-ui/GameRenderer/GameRenderer';
import { pingBackendWithData } from 'rf-lib/urls';
import Tickets from './Tickets';
import * as a from './actions';
import * as store from '../../store/actions';
import GameInfo from './GameInfo';
import { WheelEvent } from '@lykkehjulet/db-model';

const GameContainer = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: flex-start;
`;

const TicketsAndInfoContainer = styled.div`
    display: flex;
    flex: 2;
    flex-direction: column;
    justify-content: flex-start;
`;

const WheelContainer = styled.div`
    display: flex;
    flex: 5;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    pointer-events: none
`;

const PageContent = styled.div`
    display: flex;
    width: 100%;
    flex-direction: column;
    justify-content: space-between;
`;
const HeaderContainer = styled.div`
    display: flex;
    flex-direction: column;
    background: ${get('colorBackgroundSecondary')};
    margin-bottom: ${get('spacingXs')};
    padding: ${get('spacingXs')};
`;
const HeaderRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
`;

const GameInfoStateWrapper = styled.div`
    display: flex;
`;

function formatGameState(gameState: string) {
  switch (gameState.toUpperCase()) {
    case 'NOT STARTED': {
      return 'Ikke Startet';
    }
    case 'STARTED': {
      return 'Startet';
    }
    case 'PAUSED': {
      return 'Pauset';
    }
    case 'ENDED': {
      return 'Avsluttet';
    }
    case null as any: {
      return '-';
    }
    default: {
      return gameState;
    }
  }
}

const GameHeader = ({
  game,
  gameRunner
}: {
  game: any;
  gameRunner: any;
}) => (
  <HeaderContainer>
    <HeaderRow>
      <TextLarge>{game.gameName}</TextLarge>
    </HeaderRow>
    <HeaderRow>
      <GameInfoStateWrapper>
        {formatGameState(gameRunner.gameState)}
      </GameInfoStateWrapper>
      {new Date(game.startTime).toLocaleString([], {
        dateStyle: 'short',
        timeStyle: 'short'
      })}
    </HeaderRow>
  </HeaderContainer>

);

GameHeader.propTypes = {
  game: PropTypes.object.isRequired,
  gameRunner: PropTypes.object.isRequired,
};

function wakeUp(count = 1) {
  if (count > 10) {
    console.log('Wake-up failed too many times. Giving up...');
    return false;
  }
  console.log('Wake-up #', count);
  fetch(pingBackendWithData((`wake-${count}-${new Date()}` as any).replaceAll(' ', '')))
    .then(((response: any, dispatch: any) => {
      console.log('Ping response:', response);
      if (response.ok) {
        console.log('Wake-up success');
      } else {
        console.log('Wake-up failed:', response.error);
        setTimeout(() => {
          wakeUp(count + 1);
        }, 500);
      }
      return response;
    }) as any)
    .catch((err: any) => {
      console.log('Caught ping exception', err);
      setTimeout(() => {
        wakeUp(count + 1);
      }, 500);
    });
  return true;
}

const currentTimeIsPast = (startTime: string | number | Date) => new Date(startTime).getTime() < Date.now();

type GameProps = {
  addGameListener: (id: string) => void;
  addGameRunnerListener: (id: string) => void;
  addTicketsListener: (id: string) => void;
  fetchAllPlayers: () => void;
  handleBuy: (gameId: string, playerId: string) => void;
  handleSell: (gameId: string, playerId: string) => void;
  removeGameListener: (id: string) => void;
  removeGameRunnerListener: (id: string) => void;
  removeTicketsListener: (id: string) => void;
  setCurrentAnimation: (roundNumber: number) => void;
  setIsSpinning: (value: boolean) => void;
  startGame: (id: string) => void;
  reanimate: (id: string) => void;
  authUser: any;
  currentGame: any;
  gameRunner: any;
  animationState: any;
  lastShownAnimation: number;
  playersRemoteData: any;
  tickets: Record<string, number>;
};

const Game = ({ ...props }: GameProps) => {
  const { id } = useParams() as { id: string };
  const location = useLocation();
  const gameRenderer = useRef<{ animateRound:(event: WheelEvent, roundNumber: number) => void } | null>(null);
  const [canForceStartGame, setCanForceStartGame]
    = useState(false);

  const onAnimationFinished = (roundNumber: number) => {
    props.setCurrentAnimation(roundNumber);
  };

  useEffect(
    () => {
      props.fetchAllPlayers();
    },
    []
  );

  useEffect(
    () => {
      const currentRound = props.lastShownAnimation + 1;
      const currentEvent = props.gameRunner.events
        ? props.gameRunner.events[currentRound - 1]
        : undefined;

      if (currentEvent && !props.animationState.isSpinning && gameRenderer.current) {
        props.setIsSpinning(true);
        gameRenderer.current.animateRound(currentEvent, currentRound);
      }
    },
    [props.gameRunner.events, props.animationState.isSpinning, props.lastShownAnimation]
  );

  useEffect(
    () => {
      props.addGameListener(id);
      props.addGameRunnerListener(id);
      props.addTicketsListener(id);
      return () => {
        props.removeGameListener(id);
        props.removeGameRunnerListener(id);
        props.removeTicketsListener(id);
      };
    },
    [id]
  );

  useInterval(() => {
    if (props.currentGame?.data?.startTime) {
      setCanForceStartGame(currentTimeIsPast(props.currentGame.data.startTime));
    }
  }, [2000]);

  return (
    <Page>
      <RemoteData type={props.currentGame}
                  loading={() => <span>Henter spill...</span>}
                  failure={(err: any) => <span>Feil under henting: {err}</span>}
                  success={(game: any) => (
                    (!isAuthorized('player', game.groupID, props.authUser))
                      ? <NotAuthorized
                        message="Du er ikke registrert som spiller av denne spillergruppen"/>
                      : (
                        <PageContent>
                          <GameContainer>
                            <TicketsAndInfoContainer>
                              <GameHeader game={game} gameRunner={props.gameRunner}/>
                              {(props.gameRunner.gameState === 'Not started') &&
                                (
                                  <Tickets
                                    gameId={id}
                                    currentGame={game}
                                    prohibitNegativeBalance={game.prohibitNegativeBalance}
                                    playerId={props.authUser.uid}
                                    ticketCount={props.tickets[props.authUser.uid]}
                                    handleBuy={props.handleBuy}
                                    handleSell={props.handleSell}/>
                                )}

                              {(props.gameRunner.gameState === 'Not started' &&
                                  (isAuthorized('gameMaster', game.groupID, props.authUser) || canForceStartGame) &&
                                  wakeUp()) &&
                                (
                                  <Button title="Start spill" onClick={() => props.startGame(id)}/>
                                )}
                              {(props.gameRunner.gameState === 'Ended' &&
                                  isAuthorized('gameMaster', game.groupID, props.authUser)) &&
                                (
                                  <Button title="Reprise" onClick={() => props.reanimate(id)}/>
                                )}

                              <RemoteData type={props.playersRemoteData}
                                          failure={(err: any) =>
                                            <span>Feil under henting av spillere: {err}</span>}
                                          success={(players: any[]) =>
                                            (
                                              <GameInfo
                                                title={game.gameName}
                                                gameRunner={props.gameRunner}
                                                lastShownAnimation={props.lastShownAnimation}
                                                game={game}
                                                allPlayers={players}/>
                                            )}
                              />
                            </TicketsAndInfoContainer>
                            <WheelContainer id="wheelContainer">
                              <RemoteData type={props.playersRemoteData}
                                          failure={(err: any) =>
                                            <span>Feil under henting av spillere: {err}</span>}
                                          success={(players: any[]) => (
                                            <GameRenderer
                                              key={`${id}${location.search}`}
                                              ref={gameRenderer}
                                              gameId={id}
                                              game={game}
                                              numberOfPrizes={game.numberOfPrizes}
                                              allPlayers={players}
                                              isBehind={props.animationState.isBehind}
                                              winners={props.animationState.animationGameState === 'Ended' ? props.gameRunner.winnerList.map((w: any) => w.playerId) : null}
                                              remainingPlayers={
                                                props.gameRunner.gameState === 'Not started' ?
                                                  game.playersWithTickets :
                                                  props.animationState.remainingPlayers
                                              }
                                              onAnimationFinished={onAnimationFinished}/>
                                          )}
                              />
                            </WheelContainer>

                          </GameContainer>
                        </PageContent>
                      )
                  )}
      />
    </Page>
  );
};

Game.propTypes = {
  addGameListener: PropTypes.func.isRequired,
  addGameRunnerListener: PropTypes.func.isRequired,
  addTicketsListener: PropTypes.func.isRequired,
  fetchAllPlayers: PropTypes.func.isRequired,
  handleBuy: PropTypes.func.isRequired,
  handleSell: PropTypes.func.isRequired,
  removeGameListener: PropTypes.func.isRequired,
  removeGameRunnerListener: PropTypes.func.isRequired,
  removeTicketsListener: PropTypes.func.isRequired,
  setCurrentAnimation: PropTypes.func.isRequired,
  setIsSpinning: PropTypes.func.isRequired,
  startGame: PropTypes.func.isRequired,
  reanimate: PropTypes.func.isRequired,
  authUser: PropTypes.object.isRequired,
  currentGame: PropTypes.object.isRequired,
  gameRunner: PropTypes.object.isRequired,
  animationState: PropTypes.object.isRequired,
  playersRemoteData: PropTypes.object.isRequired,
  lastShownAnimation: PropTypes.number.isRequired,
  tickets: PropTypes.object.isRequired,
};

const mapStateToProps = ({
  session,
  games
}: any) => ({
  authUser: session.authUser,
  gameRunner: games.gameRunnerState,
  animationState: games.animationState,
  lastShownAnimation: games.animationState.lastShownAnimation,
  tickets: games.ticketsState,
  currentGame: games.currentGameState,
  playersRemoteData: session.playersRemoteData,
});

const mapDispatchToProps = (dispatch: any) => ({
  startGame: a.startGame(dispatch),
  reanimate: a.reanimate(dispatch),
  fetchAllPlayers: store.fetchAllPlayers(dispatch),
  setCurrentAnimation: a.setCurrentAnimation(dispatch),
  setIsSpinning: a.setIsSpinning(dispatch),
  addGameListener: a.addGameListener(dispatch),
  removeGameListener: a.removeGameListener(dispatch),
  addGameRunnerListener: a.addGameRunnerListener(dispatch),
  removeGameRunnerListener: a.removeGameRunnerListener(dispatch),
  addTicketsListener: a.addTicketsListener(dispatch),
  removeTicketsListener: a.removeTicketsListener(dispatch),
  handleBuy: a.handleBuy(dispatch),
  handleSell: a.handleSell(dispatch),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Game as any);
